import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Card, Typography, Table, Tooltip } from "antd";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ChartTooltip,
  Legend,
  ChartDataLabels
);

const { Text } = Typography;

export default function BarChart({ title, labels, datasets, rawData }) {
  const truncatedLabels = labels.map((l) =>
    l.length > 8 ? l.substring(0, 8) + ".." : l
  );

  const formatAbbreviated = (value) => {
    if (!value) return "0";
    if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
    if (value >= 1000) return (value / 1000).toFixed(1) + "k";
    return Math.round(value);
  };

  const formatCurrency = (value) => 
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(value);

  const chartData = {
    labels: truncatedLabels,
    datasets: datasets || [],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { boxWidth: 8, font: { size: 9 }, padding: 4 }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
        }
      },
      datalabels: {
        display: (context) => context.datasetIndex === context.chart.data.datasets.length - 1,
        anchor: "end",
        align: "top",
        offset: 2,
        rotation: -90,
        font: { size: 9, weight: "bold" },
        color: "#595959",
        formatter: (value, context) => {
          const total = context.chart.data.datasets.reduce((sum, ds) => sum + ds.data[context.dataIndex], 0);
          return formatAbbreviated(total);
        }
      }
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { callback: formatAbbreviated, font: { size: 8 } }
      },
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 8 } }
      }
    },
    layout: {
      padding: { top: 35 }
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'usuario_nombre',
      key: 'usuario_nombre',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text strong style={{ fontSize: '10px' }}>
            {text.length > 10 ? text.substring(0, 10) + ".." : text}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_monto',
      key: 'total_monto',
      align: 'right',
      render: (val) => <Text style={{ fontSize: '10px' }}>{formatAbbreviated(val)}</Text>,
    },
    {
      title: 'Verificador',
      key: 'user',
      align: 'right',
      render: (_, record) => <Text type="secondary" style={{ fontSize: '9px' }}>{formatAbbreviated(record.total_monto * 0.6)}</Text>,
    },
    {
      title: 'Empresa',
      key: 'company',
      align: 'right',
      render: (_, record) => <Text type="secondary" style={{ fontSize: '9px' }}>{formatAbbreviated(record.total_monto * 0.4)}</Text>,
    },
  ];

  return (
    <Card 
      title={<Text style={{ fontSize: '13px', fontWeight: 600, color: '#262626' }}>{title}</Text>} 
      size="small" 
      style={{ borderRadius: 12, height: "100%", boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} 
      bodyStyle={{ padding: "12px" }}
    >
      {datasets && datasets.length && datasets[0].data.length ? (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1.5 1 300px', height: "240px" }}>
            <Bar data={chartData} options={options} />
          </div>
          <div style={{ flex: '1 1 250px', minWidth: '250px' }}>
            <style>
              {`
                .compact-side-table .ant-table-cell {
                  padding: 4px 4px !important;
                }
                .compact-side-table .ant-table-thead > tr > th {
                  background: #fdfdfd;
                  font-size: 9px;
                  padding: 4px 4px !important;
                }
              `}
            </style>
            <Table
              className="compact-side-table"
              dataSource={rawData}
              columns={columns}
              pagination={false}
              size="small"
              scroll={{ y: 200 }}
              rowKey="UsuarioID"
              bordered={false}
            />
          </div>
        </div>
      ) : (
        <div style={{ height: "240px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Text type="secondary">No hay datos para mostrar</Text>
        </div>
      )}
    </Card>
  );
}
